import { useEffect } from 'react';
import { useSensitiveDataRegistry } from '@/contexts/SensitiveDataRegistry';
import { useClients } from '@/hooks/useClients';
import { useProjectsV2 } from '@/hooks/useProjectsV2';
import { useContractorRates } from '@/hooks/useContractorRates';

/**
 * Hook that automatically registers sensitive data from common data sources.
 * Place this once near the top of your app (inside the SensitiveDataRegistryProvider).
 *
 * This captures:
 * - All client names
 * - All project names
 * - Contact names and emails from clients
 */
export function useAutoRegisterSensitiveData() {
  const { register, registerMany } = useSensitiveDataRegistry();
  const { clients } = useClients();
  const { projects } = useProjectsV2();
  const { data: contractors } = useContractorRates();

  // Register client names
  useEffect(() => {
    if (clients && clients.length > 0) {
      const clientNames = clients.map((c) => c.name);
      registerMany(clientNames, 'clientName');
    }
  }, [clients, registerMany]);

  // Register project names
  useEffect(() => {
    if (projects && projects.length > 0) {
      const projectNames = projects.map((p) => p.name);
      registerMany(projectNames, 'projectName');

      // Also register ClickUp list names if they differ
      const clickupNames = projects
        .map((p) => p.clickup_list_name)
        .filter((name): name is string => !!name);
      if (clickupNames.length > 0) {
        registerMany(clickupNames, 'projectName');
      }
    }
  }, [projects, registerMany]);

  // Register contractor names
  useEffect(() => {
    if (contractors && contractors.length > 0) {
      const contractorNames = contractors
        .map((c) => c.org_person?.name)
        .filter((name): name is string => !!name);
      if (contractorNames.length > 0) {
        registerMany(contractorNames, 'contactName');
      }
    }
  }, [contractors, registerMany]);
}

/**
 * Hook to register a specific client's sensitive data.
 * Use this on client detail pages to capture contacts, emails, etc.
 */
export function useRegisterClientSensitiveData(client: {
  name?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  billing_contact_name?: string | null;
  billing_contact_email?: string | null;
  phone?: string | null;
  address?: string | null;
} | null | undefined) {
  const { register } = useSensitiveDataRegistry();

  useEffect(() => {
    if (!client) return;

    if (client.name) register(client.name, 'clientName');
    if (client.primary_contact_name) register(client.primary_contact_name, 'contactName');
    if (client.primary_contact_email) register(client.primary_contact_email, 'email');
    if (client.billing_contact_name) register(client.billing_contact_name, 'contactName');
    if (client.billing_contact_email) register(client.billing_contact_email, 'email');
    if (client.phone) register(client.phone, 'phone');
    if (client.address) register(client.address, 'address');
  }, [client, register]);
}

/**
 * Hook to register an array of contacts/team members
 */
export function useRegisterContactsSensitiveData(
  contacts: Array<{
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  }> | null | undefined
) {
  const { registerMany, register } = useSensitiveDataRegistry();

  useEffect(() => {
    if (!contacts || contacts.length === 0) return;

    const names = contacts.map((c) => c.name).filter((n): n is string => !!n);
    const emails = contacts.map((c) => c.email).filter((e): e is string => !!e);
    const phones = contacts.map((c) => c.phone).filter((p): p is string => !!p);

    if (names.length > 0) registerMany(names, 'contactName');
    if (emails.length > 0) registerMany(emails, 'email');
    phones.forEach((p) => register(p, 'phone'));
  }, [contacts, register, registerMany]);
}

/**
 * Hook to register project-specific sensitive data
 */
export function useRegisterProjectSensitiveData(project: {
  name?: string | null;
  clickup_list_name?: string | null;
} | null | undefined) {
  const { register } = useSensitiveDataRegistry();

  useEffect(() => {
    if (!project) return;

    if (project.name) register(project.name, 'projectName');
    if (project.clickup_list_name) register(project.clickup_list_name, 'projectName');
  }, [project, register]);
}
